import { Divider, mergeClasses, tokens } from '@fluentui/react-components';
import { Fragment, type ReactNode, useMemo } from 'react';
import { Container } from './container/index.js';
import { useStyles } from './styles.js';

const HEADER_COLOR = {
    green: '#2C7960',
    lime: '#B3DC1F',
    orange: '#FF6D44',
    grey: '#5F5E5E',
    default: '#2C7960',
} as const;

type SectionContent = {
    element: ReactNode | null;
    removeContentPadding?: boolean;
    size?: `${number}fr` | `${number}px` | `${number}rem` | 'max-content' | 'auto';
    showGradient?: boolean;
    showDivider?: boolean;
};

type SectionProps = {
    header?: {
        label: string | ReactNode;
        color?: keyof typeof HEADER_COLOR;
        icon: JSX.Element;
        actions?: ReactNode | Array<ReactNode>;
    };
    footer?: ReactNode;
    fillContainer?: boolean;
} & (
    | {
          children?: ReactNode;
          childrenArray?: never;
          removeContentPadding?: boolean;
          showGradient?: boolean;
      }
    | {
          children?: never;
          childrenArray: Array<SectionContent>;
          removeContentPadding?: never;
          showGradient?: never;
      }
);

export function Section({
    children,
    childrenArray,
    header,
    footer,
    fillContainer,
    removeContentPadding,
    showGradient,
}: SectionProps) {
    const styles = useStyles();

    const gridRows = useMemo(() => {
        const rows = [];

        if (header) {
            rows.push('max-content', 'max-content');
        }

        if (children) {
            // Use 1fr only if fillContainer is true, otherwise use max-content
            rows.push(fillContainer ? '1fr' : 'max-content');
        }

        if (childrenArray) {
            childrenArray.forEach((child, index) => {
                if (!child.element) {
                    return;
                }

                if (child.showDivider && index > 0) {
                    rows.push('max-content');
                }

                if (child.size) {
                    rows.push(child.size);
                } else {
                    rows.push('1fr');
                }
            });
        }

        if (footer) {
            rows.push('max-content', 'max-content');
        }

        return rows.join(' ');
    }, [children, childrenArray, fillContainer, footer, header]);

    return (
        <div
            className={mergeClasses(styles.container, !!fillContainer && styles.fillParent)}
            style={{
                gridTemplateRows: gridRows,
            }}
        >
            {!!header && (
                <>
                    <div className={styles.header}>
                        <div
                            className={styles.headerStrip}
                            style={{
                                backgroundColor: HEADER_COLOR[header.color ?? 'default'],
                            }}
                        />

                        <div className={styles.section}>
                            <div className={styles.headerLabel}>
                                <div className={styles.headerLabelIcon}>{header.icon}</div>

                                <div
                                    className={styles.headerLabelText}
                                    title={typeof header.label === 'string' ? header.label : undefined}
                                >
                                    {header.label}
                                </div>
                            </div>

                            {!!header.actions && (
                                <div className={styles.headerActions}>
                                    {Array.isArray(header.actions)
                                        ? header.actions.map((action, index) => <div key={index}>{action}</div>)
                                        : header.actions}
                                </div>
                            )}
                        </div>
                    </div>

                    <Divider />
                </>
            )}

            {children && (
                <Container
                    removePadding={!!removeContentPadding}
                    gradientColor={showGradient ? tokens.colorNeutralBackground1 : undefined}
                >
                    {children}
                </Container>
            )}

            {childrenArray &&
                childrenArray.map((child, index) => {
                    return !child.element ? null : (
                        <Fragment key={index}>
                            {child.showDivider && index > 0 && <Divider />}

                            <Container
                                key={index}
                                removePadding={!!child.removeContentPadding}
                                gradientColor={child.showGradient ? tokens.colorNeutralBackground1 : undefined}
                            >
                                {child.element}
                            </Container>
                        </Fragment>
                    );
                })}

            {!!footer && (
                <>
                    <Divider />

                    <div className={styles.section}>{footer}</div>
                </>
            )}
        </div>
    );
}
